import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    CircularProgress,
    Card,
    CardContent,
    Alert,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    InputAdornment,
    IconButton,
    FormControl,
    OutlinedInput
} from "@mui/material";
import { publicApiRequest } from "../common/common";
import BiddingForm from "./Home/Project/BiddingForm";
import { Error, ReportProblem, Visibility, VisibilityOff } from "@mui/icons-material";
import { showNotification } from "../common/Notification/ToastifyNotification";
import moment from "moment";
import { LoadingPage } from "../common/LoadingPage";

const TechnicalForm = () => {
    const { formid, contactid } = useParams();
    const [showPasswordModal, setShowPasswordModal] = useState(true);
    const [password, setPassword] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState("");
    const [isError, setIsError] = useState(false);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [vendor, setVendor] = useState(null);
    const [formData, setFormData] = useState(null);
    const [responseData, setResponseData] = useState(null);
    const [validationModalOpen, setValidationModalOpen] = useState(false);
    const [validationErrors, setValidationErrors] = useState([]);
    const [dataLoading, setDataLoading] = useState(false);
    const [responseId, setReponseId] = useState(null);
    const intervalRef = useRef();
    const [showPassword, setShowPassword] = React.useState(false);
    const [status, setStatus] = useState(null);

    const handleClickShowPassword = () => setShowPassword((show) => !show);

    const handlePasswordSubmit = async () => {
        if (!password && !localStorage.getItem('password')) {
            setError("Password is required");
            return;
        }

        setIsLoading(true);
        setError("");

        try {
            const response = await publicApiRequest('post', `/po-form/get-po-form`, {
                "formId": formid,
                "venderContactId": contactid,
                "passkey": password || localStorage.getItem('password')
            });

            if (response.data) {
                localStorage.setItem('password', password || localStorage.getItem('password'));
                const { form_data, purchase_module } = response.data.data;
                if (purchase_module.end_date && new Date(purchase_module.end_date) < new Date()) {
                    setError("Due to bid closed you can't access it.");
                    setIsError(true);
                } else if (purchase_module.start_date && new Date(purchase_module.start_date) > new Date()) {
                    setError("Due to bid is not started so you can't access it.");
                    setIsError(true);
                } else {
                    localStorage.setItem('public-token', response.data.token);
                    const vendorResponse = await publicApiRequest('get', `/vender-response/public-by-contact/${formid}/${contactid}`);
                    if (vendorResponse.data.data?.vender_response_id) {
                        setReponseId(vendorResponse.data.data?.vender_response_id);
                        setStatus(vendorResponse.data.data.status);
                        setFormData(vendorResponse.data.data?.response);
                    } else {
                        setFormData(form_data);
                    }
                    setResponseData(response.data.data);
                    setVendor(response.data.vender);
                    setIsAuthenticated(true);
                    setShowPasswordModal(false);
                }
            } else {
                setError(response.message || "Failed to generate token");
            }
        } catch (err) {
            setIsAuthenticated(false);
            setShowPasswordModal(true);
            localStorage.removeItem('password');
            setError("Error generating token. Please try again.");
        } finally {
            setIsLoading(false);
        }
    };

    const handleCloseModal = () => {
        setShowPasswordModal(false);
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            handlePasswordSubmit();
        }
    };

    useEffect(() => {
        if (localStorage.getItem('password')) {
            handlePasswordSubmit();
        }
    }, [localStorage.getItem('password')]);


    const canSaveForm = (formData) => {
        const hasItemData = formData.items && formData.items.some(item => {
            if (item.manufacturer?.trim()) return true;
            if (item.model?.trim()) return true;
            if (item.sections && item.sections.some(section => {
                if (section.sectionName === 'Attachments') return false;
                return section.requirements && section.requirements.some(req =>
                    req.response?.trim() ||
                    req.remarks?.trim() ||
                    (req.attachments && req.attachments.length > 0)
                );
            })) return true;

            return false;
        });
        const hasVendorDocs = formData.vendorDocuments?.some(doc =>
            doc.attachments?.length > 0
        );

        return !!(hasItemData || hasVendorDocs);
    };

    const handleSave = async () => {
        if (canSaveForm(formData)) {
            try {
                setDataLoading(true);

                formData.vendor = vendor.name;
                formData.date = moment().format('DD-MM-YYYY hh:mm A');
                const data = {
                    response: formData,
                    vender_email: vendor.email,
                    purchase_module_id: responseData.purchase_module.purchase_module_id,
                    project_id: responseData.project_id,
                    organization_id: responseData.organization_id,
                    po_form_id: formid,
                    created_by: responseData.created_by,
                    status: "draft",
                    vendor_id: vendor?.vender?.product_provider_id
                };

                let response;
                if (responseId) {
                    response = await publicApiRequest('put', `/vender-response/${responseId}`, data);
                } else {
                    response = await publicApiRequest('post', '/vender-response/save', data);
                }
                if (response.data) {
                    setDataLoading(false);
                    showNotification('Data saved successfully!', 'success');
                    handlePasswordSubmit();
                }
            } catch (error) {
                setDataLoading(false);
                showNotification(error?.response?.data.error || error.message, 'error');
            } finally {
                setDataLoading(false);
            }
        } else {
            showNotification('Please add some data before saving.', 'error');
        }
    };

    const handleAutoSave = useCallback(async () => {
        if (canSaveForm(formData)) {
            try {
                formData.vendor = vendor.name;
                formData.date = moment().format('DD-MM-YYYY hh:mm A');
                const data = {
                    response: formData,
                    vender_email: vendor.email,
                    purchase_module_id: responseData.purchase_module.purchase_module_id,
                    project_id: responseData.project_id,
                    organization_id: responseData.organization_id,
                    status: status !== null ? status : "draft",
                    po_form_id: formid,
                    created_by: responseData.created_by,
                    vendor_id: vendor?.vender?.product_provider_id
                };
                let response;
                if (responseId) {
                    response = await publicApiRequest('put', `/vender-response/${responseId}`, data);
                } else {
                    response = await publicApiRequest('post', '/vender-response/save', data);
                }
                if (response.data?.data?.vender_response_id) {
                    setReponseId(response.data.data.vender_response_id);
                }
            } catch (error) {
                console.error('Auto-save failed:', error);
            }
        }
    }, [formData]);

    useEffect(() => {
        intervalRef.current = setInterval(() => {
            const canSave = canSaveForm(formData);
            if (canSave) {
                handleAutoSave();
            }
        }, 5 * 60 * 1000);

        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
        };
    }, [formData, handleAutoSave]);


    if (!isAuthenticated && showPasswordModal && !localStorage.getItem('password')) {
        return (
            <Dialog
                open={showPasswordModal}
                onClose={handleCloseModal}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    <Typography variant="h6" component="div">
                        Access Technical Specifications
                    </Typography>
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ pt: 2 }}>
                        <Typography variant="body2" color="textSecondary" gutterBottom>
                            Please enter your password to access the Technical Specifications.
                        </Typography>
                        <FormControl size="small" fullWidth variant="outlined">
                            <OutlinedInput
                                label=""
                                placeholder='Password'
                                autoComplete="new-password"
                                id="standard-adornment-password"
                                type={showPassword ? 'text' : 'password'}
                                value={password}
                                onChange={(e) => {
                                    setPassword(e.target.value);
                                    setError("");
                                }}
                                onKeyPress={handleKeyPress}
                                error={!!error}
                                helperText={error}
                                disabled={isLoading}
                                sx={{ mt: 1 }}
                                autoFocus
                                endAdornment={
                                    <InputAdornment position="end">
                                        <IconButton
                                            aria-label={
                                                showPassword ? 'hide the password' : 'display the password'
                                            }
                                            onClick={handleClickShowPassword}
                                            edge="end"
                                        >
                                            {showPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                }
                            />
                        </FormControl>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={handleCloseModal}
                        color="inherit"
                        disabled={isLoading}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handlePasswordSubmit}
                        variant="contained"
                        color="primary"
                        disabled={isLoading}
                        startIcon={isLoading ? <CircularProgress size={20} /> : null}
                    >
                        {isLoading ? "Generating Token..." : "Access"}
                    </Button>
                </DialogActions>
            </Dialog>
        );
    }

    if (isError) {
        return (
            <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                minHeight="280px"
                px={2}
            >
                <Card
                    sx={{
                        maxWidth: 450,
                        width: '100%',
                        textAlign: 'center',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                    }}
                >
                    <CardContent sx={{ p: 4 }}>
                        <Box sx={{ color: 'warning.main', mb: 2 }}>
                            <ReportProblem sx={{ fontSize: 56 }} />
                        </Box>
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mb: 3, lineHeight: 1.6 }}
                        >
                            {error}
                        </Typography>
                    </CardContent>
                </Card>
            </Box>
        );
    }

    if (!isAuthenticated && !showPasswordModal) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                <CircularProgress />
                <Typography variant="body1" sx={{ ml: 2 }}>
                    Checking authentication...
                </Typography>
            </Box>
        );
    }

    const validateFormData = (formData) => {
        const errors = [];

        // 1. Validate Place field
        if (!formData.place || formData.place.trim() === '') {
            errors.push('Place field is required');
        }

        // 2. Validate Acknowledgement
        if (!formData.acknowledgement?.reviewed) {
            errors.push('Please acknowledge that you have reviewed and understood the RFP');
        }

        if (!formData.acknowledgement?.accepted) {
            errors.push('Please accept the terms and conditions');
        }

        // 3. Validate Items (Manufacturer and Model for each item)
        if (!formData.items || formData.items.length === 0) {
            errors.push('At least one item is required');
        } else {
            formData.items.forEach((item, index) => {
                if (!item.manufacturer || item.manufacturer.trim() === '') {
                    errors.push(`Manufacturer is required for ${item.name || `Item ${index + 1}`}`);
                }

                if (!item.model || item.model.trim() === '') {
                    errors.push(`Model is required for ${item.name || `Item ${index + 1}`}`);
                }
            });
        }

        return {
            isValid: errors.length === 0,
            errors
        };
    };

    const handleSubmit = async () => {
        const validation = validateFormData(formData);

        if (!validation.isValid) {
            setValidationErrors(validation.errors);
            setValidationModalOpen(true);
            return;
        }
        try {
            setDataLoading(true);
            formData.vendor = vendor.name;
            formData.date = moment().format('DD-MM-YYYY hh:mm A');
            const data = {
                response: formData,
                vender_email: vendor.email,
                purchase_module_id: responseData.purchase_module.purchase_module_id,
                project_id: responseData.project_id,
                organization_id: responseData.organization_id,
                status: "completed",
                po_form_id: formid,
                created_by: responseData.created_by,
                vendor_id: vendor?.vender?.product_provider_id
            };
            let response;
            if (responseId) {
                response = await publicApiRequest('put', `/vender-response/${responseId}`, data);
            } else {
                response = await publicApiRequest('post', '/vender-response/save', data);
            }
            if (response.data) {
                showNotification('Form submitted successfully!', 'success');
                handlePasswordSubmit();
                setDataLoading(false);
            }
        } catch (error) {
            setDataLoading(false);
            showNotification(error?.response?.data.error || error.message, 'error');
        }
    }

    if (isAuthenticated) {
        return (
            <>
                <Dialog open={validationModalOpen} onClose={() => setValidationModalOpen(false)} maxWidth="md" fullWidth>
                    <DialogTitle>
                        <Box display="flex" alignItems="center">
                            <Error color="error" sx={{ mr: 1 }} />
                            <Typography variant="h6">
                                Form Validation Errors ({validationErrors.length})
                            </Typography>
                        </Box>
                    </DialogTitle>

                    <DialogContent>
                        <Alert severity="error" sx={{ mb: 2 }}>
                            Please fix the following issues before submitting:
                        </Alert>

                        <List dense>
                            {validationErrors.map((error, index) => (
                                <ListItem key={index}>
                                    <ListItemIcon>
                                        <Error color="error" fontSize="small" />
                                    </ListItemIcon>
                                    <ListItemText
                                        primary={error}
                                        primaryTypographyProps={{ variant: 'body2' }}
                                    />
                                </ListItem>
                            ))}
                        </List>
                    </DialogContent>

                    <DialogActions>
                        <Button onClick={() => setValidationModalOpen(false)} variant='outlined'>
                            Close
                        </Button>
                    </DialogActions>
                </Dialog>
                {dataLoading && (<LoadingPage isLoading={dataLoading} />)}
                <BiddingForm
                    formData={formData}
                    setFormData={setFormData}
                    isPublic
                    handleSubmit={handleSubmit}
                    handleSave={handleSave}
                />
            </>
        );
    }
};

export default TechnicalForm;